/*
  <shintop-recently-viewed> — records product visits and renders the trail.

  Storage: localStorage 'shintop:recently-viewed', an array of {h, t} (handle, title),
  newest first, capped at 12 stored / 8 rendered. Everything is wrapped so private
  browsing or disabled storage silently degrades to nothing.

  Rendering: ONE Section Rendering API request on the search route; the section file
  renders the finished band server-side. The primary query asks by handle
  (q=handle:a OR handle:b); if the search backend returns no usable cards for that
  syntax, one fallback request asks by quoted title. Either way the returned cards are
  filtered to the stored handles in the DOM, so precision never depends on search
  behaviour, then reordered newest-first and capped.

  Started after `load`, on idle — or sooner, when the band nears the viewport — not on approach
  alone (S05-002, audit 2026-10-14 chunk 12): fetched as the shopper scrolled towards it, the band
  arrived on screen and pushed the footer down. The full reasoning is in
  shintop-recommendations.js, whose `startSoon` this one copies; keep the two in step.
*/
if (!customElements.get('shintop-recently-viewed')) {
  const STORAGE_KEY = 'shintop:recently-viewed';
  const STORE_CAP = 12;
  const RENDER_CAP = 8;

  /* Runs `run` once: after `load`, when the browser is idle — or sooner, if the band nears the
     viewport first. The twin of shintop-recommendations.js's. */
  const startSoon = (el, run) => {
    let started = false;
    const go = () => {
      if (started || !el.isConnected) return;
      started = true;
      el.observer?.disconnect();
      run();
    };
    el.observer = new IntersectionObserver((entries) => entries[0].isIntersecting && go(), {
      rootMargin: '0px 0px 600px 0px',
    });
    el.observer.observe(el);
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(go, { timeout: 2000 }) : setTimeout(go, 200));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  };

  customElements.define(
    'shintop-recently-viewed',
    class ShintopRecentlyViewed extends HTMLElement {
      connectedCallback() {
        const trail = this.recordVisit();
        if (!trail.length) return;
        startSoon(this, () => this.load(trail));
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      readTrail() {
        try {
          const raw = JSON.parse(localStorage.getItem(STORAGE_KEY));
          if (!Array.isArray(raw)) return [];
          return raw.filter((entry) => entry && typeof entry.h === 'string');
        } catch {
          return [];
        }
      }

      /* Puts the current product at the head of the trail, returns the trail WITHOUT it —
         the list of other products worth showing on this page. */
      recordVisit() {
        const current = this.dataset.currentHandle;
        let trail = this.readTrail();
        if (current) {
          trail = trail.filter((entry) => entry.h !== current);
          const updated = [{ h: current, t: this.dataset.currentTitle || '' }, ...trail].slice(0, STORE_CAP);
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          } catch {
            /* storage unavailable — still render from what we read */
          }
        }
        return trail.slice(0, RENDER_CAP);
      }

      async load(trail) {
        const byHandle = trail.map((entry) => `handle:${entry.h}`).join(' OR ');
        let cards = await this.fetchCards(byHandle, trail);

        if (!cards.size) {
          const titles = trail.filter((entry) => entry.t).map((entry) => `"${entry.t.replaceAll('"', '')}"`);
          if (titles.length) cards = await this.fetchCards(titles.join(' OR '), trail);
        }
        if (!cards.size) return;

        const band = this.band;
        const track = band.querySelector('.shintop-carousel__track');
        const recent = trail.filter((entry) => cards.has(entry.h)).map((entry) => cards.get(entry.h));
        /* Sold out goes last, every carousel row (Naoya, 2026-10-01). The row snippet drew it last and
           the recency order above undid that, so the first card of a buy-again row could be one the
           shopper cannot buy (S09-001, audit 2026-10-14). Two passes keep recency inside each half —
           the row snippet's own rule, read off the card's `data-shintop-available`. */
        const sold = (card) => card.dataset.shintopAvailable === 'false';
        const ordered = [...recent.filter((card) => !sold(card)), ...recent.filter(sold)];
        /* One card is not a row. A "Recently viewed" band holding a single product rendered at 17 %
           fill above the footer (2026-09-08 design audit, measured at 1440) — a heading, a carousel
           shell and a progress rail for one card the visitor has, by definition, already seen. Under
           two cards the band stays absent, exactly as it does with no trail at all. */
        if (ordered.length < 2) return;
        track.replaceChildren(...ordered);
        this.replaceChildren(band);
      }

      /* One section render; returns a handle -> card element map for the wanted trail,
         and keeps the surrounding band markup on this.band for load() to reuse. */
      async fetchCards(query, trail) {
        const wanted = new Set(trail.map((entry) => entry.h));
        const url = `${this.dataset.searchUrl}?section_id=${this.dataset.section}&type=product&q=${encodeURIComponent(query)}`;
        const cards = new Map();
        try {
          const response = await fetch(url);
          if (!response.ok) return cards;
          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const band = doc.querySelector('.shintop-section');
          if (!band) return cards;
          band.querySelectorAll('[data-shintop-handle]').forEach((card) => {
            const handle = card.dataset.shintopHandle;
            if (wanted.has(handle) && !cards.has(handle)) cards.set(handle, card);
          });
          if (cards.size) this.band = band;
        } catch {
          /* leave the band absent */
        }
        return cards;
      }
    },
  );
}
